import { useState, useEffect, useRef, useCallback } from 'react';
import { useDebounce } from '@shared/hooks/useDebounce';

import { TextField, Textarea } from '@/shared/components/TextField';
import FileUpload from '@/shared/components/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import { Button } from '@/shared/components/Buttons';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { DatePickerField } from '@/shared/components/DatePickerField';
import CreateModal from '@/shared/layout/createLayout/CreateModal';
import { useDatePickerScrollClose } from '../../hooks/useDatePickerScrollClose';

import { FACILITY_LABELS } from '../../constants/facilityLabels';
import { EQUIPMENT_LABELS } from '../../constants/labels';
import { EQUIPMENT_ERROR_MESSAGES } from '../../errors/errorMessages';
import { editEquipment } from '../../api/editEquipmentApiService';
import { getPresignedUrlsBatch } from '../../api/presignedUrlBatchApiService';
import { uploadFileToS3 } from '../../api/uploadFileToS3';
import { deleteMediaResource } from '@/epics/clientManagement/api/deleteMediaResourceApiService';
import {
  listEquipmentCategories,
  type EquipmentCategoryItem,
} from '../../api/listEquipmentCategoriesApiService';
import type { EquipmentListItem } from '../../api/listEquipmentApiService';
import type { MediaResource } from '../../types/addEquipmentType';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ExistingFileMeta {
  filename: string;
  fileSize: number;
  fileType: string;
  path: string;
  resourceId: string;
  isUploaded: boolean;
}

interface EditForm {
  name: string;
  description: string;
  category: string;
  serialNumber: string;
  modelName: string;
  dateOfProduction: Date | undefined;
}

interface EditFormErrors {
  name: string;
  description: string;
  category: string;
  imageFile: string;
  serialNumber: string;
}

export interface EditEquipmentFormProps {
  equipment: EquipmentListItem;
  onCancel: () => void;
  onSaved: (updated: EquipmentListItem) => void;
  onError: (message: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onSavingChange?: (isSaving: boolean) => void;
  initialCategoryLabel?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const inferFileType = (filename: string, hint?: string | null): string => {
  if (hint) {
    return hint;
  }
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'pdf':
      return 'application/pdf';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    default:
      return 'application/octet-stream';
  }
};

const buildImageMeta = (
  image: EquipmentListItem['image']
): ExistingFileMeta | null => {
  if (!image?.filename) {
    return null;
  }
  return {
    filename: image.filename,
    fileSize: image.fileSize ?? 0,
    fileType: inferFileType(image.filename, image.fileType),
    path: image.path ?? '',
    resourceId: image.resourceId ?? '',
    isUploaded: true,
  };
};

const buildDocsMeta = (docs: EquipmentListItem['docs']): ExistingFileMeta[] =>
  (docs ?? [])
    .filter((d): d is typeof d & { filename: string } => !!d.filename)
    .map((d) => ({
      filename: d.filename,
      fileSize: d.fileSize ?? 0,
      fileType: inferFileType(d.filename, d.fileType),
      path: d.path ?? '',
      resourceId: d.resourceId ?? '',
      isUploaded: true,
    }));

const FAKE_STEPS = 15;
const FAKE_DURATION_MS = 600;

const optionalLabel = (text: string) => (
  <>
    {text}
    <span className="text-bodySmall font-normal">
      {' '}
      {EQUIPMENT_LABELS.SECTION_OPTIONAL}
    </span>
  </>
);

// ── Component ─────────────────────────────────────────────────────────────────

export const EditEquipmentForm = ({
  equipment,
  onCancel,
  onSaved,
  onError,
  onDirtyChange,
  onSavingChange,
  initialCategoryLabel,
}: EditEquipmentFormProps) => {
  const [form, setForm] = useState<EditForm>({
    name: equipment.name,
    description: equipment.description,
    category: equipment.category ?? '',
    serialNumber: equipment.serialNumber ?? '',
    modelName: equipment.modelName ?? '',
    dateOfProduction: equipment.dateOfProduction
      ? new Date(equipment.dateOfProduction)
      : undefined,
  });
  const [errors, setErrors] = useState<EditFormErrors>({
    name: '',
    description: '',
    category: '',
    imageFile: '',
    serialNumber: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const datePickerRef = useDatePickerScrollClose(datePickerOpen, () =>
    setDatePickerOpen(false)
  );

  const [defaultCategories, setDefaultCategories] = useState<
    EquipmentCategoryItem[]
  >([]);
  const [searchCategories, setSearchCategories] = useState<
    EquipmentCategoryItem[]
  >([]);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryHasMore, setCategoryHasMore] = useState(true);
  const [categoryDefaultLoading, setCategoryDefaultLoading] = useState(false);
  const [categorySearchLoading, setCategorySearchLoading] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const debouncedCategorySearch = useDebounce(categorySearch, 500);

  useEffect(() => {
    const load = async () => {
      setCategoryDefaultLoading(true);
      try {
        const result = await listEquipmentCategories({ page: 1, limit: 10 });
        setDefaultCategories(result.data);
        setCategoryHasMore(result.hasNextPage);
        setCategoryPage(1);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setCategoryDefaultLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!debouncedCategorySearch) {
      setSearchCategories([]);
      return;
    }
    const search = async () => {
      setCategorySearchLoading(true);
      try {
        const result = await listEquipmentCategories({
          page: 1,
          limit: 10,
          search: debouncedCategorySearch,
        });
        setSearchCategories(result.data);
      } catch (err) {
        console.error('Error searching categories:', err);
      } finally {
        setCategorySearchLoading(false);
      }
    };
    search();
  }, [debouncedCategorySearch]);

  const fetchMoreCategories = useCallback(async () => {
    if (categoryDefaultLoading || !categoryHasMore) {
      return;
    }
    setCategoryDefaultLoading(true);
    const nextPage = categoryPage + 1;
    try {
      const result = await listEquipmentCategories({
        page: nextPage,
        limit: 10,
      });
      setDefaultCategories((prev) => [
        ...prev,
        ...result.data.filter((c) => !prev.some((p) => p.id === c.id)),
      ]);
      setCategoryHasMore(result.hasNextPage);
      setCategoryPage(nextPage);
    } catch (err) {
      console.error('Error fetching more categories:', err);
    } finally {
      setCategoryDefaultLoading(false);
    }
  }, [categoryDefaultLoading, categoryHasMore, categoryPage]);

  const [existingImageMeta, setExistingImageMeta] =
    useState<ExistingFileMeta | null>(() => buildImageMeta(equipment.image));
  const [existingDocsMeta, setExistingDocsMeta] = useState<ExistingFileMeta[]>(
    () => buildDocsMeta(equipment.docs)
  );
  const deletedPaths = useRef<string[]>([]);

  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [newDocFiles, setNewDocFiles] = useState<File[]>([]);

  // Fake progress: key → progress bytes (UI only, no real upload until save)
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const fakeTimers = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map()
  );

  // Re-sync when parent passes a different equipment item
  useEffect(() => {
    setForm({
      name: equipment.name,
      description: equipment.description,
      category: equipment.category ?? '',
      serialNumber: equipment.serialNumber ?? '',
      modelName: equipment.modelName ?? '',
      dateOfProduction: equipment.dateOfProduction
        ? new Date(equipment.dateOfProduction)
        : undefined,
    });
    setErrors({
      name: '',
      description: '',
      category: '',
      imageFile: '',
      serialNumber: '',
    });
    setExistingImageMeta(buildImageMeta(equipment.image));
    setExistingDocsMeta(buildDocsMeta(equipment.docs));
    setNewImageFile(null);
    setNewDocFiles([]);
    setUploadProgress({});
    deletedPaths.current = [];
    fakeTimers.current.forEach((id) => clearInterval(id));
    fakeTimers.current.clear();
  }, [equipment.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const initialImageCount = buildImageMeta(equipment.image) ? 1 : 0;
  const initialDocsCount = buildDocsMeta(equipment.docs).length;

  const isDirty =
    form.name !== equipment.name ||
    form.description !== equipment.description ||
    form.category !== (equipment.category ?? '') ||
    form.serialNumber !== (equipment.serialNumber ?? '') ||
    form.modelName !== (equipment.modelName ?? '') ||
    form.dateOfProduction?.toISOString() !==
      (equipment.dateOfProduction
        ? new Date(equipment.dateOfProduction).toISOString()
        : undefined) ||
    newImageFile !== null ||
    newDocFiles.length > 0 ||
    (existingImageMeta === null && initialImageCount > 0) ||
    existingDocsMeta.length < initialDocsCount;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const updateField = (
    field: keyof Pick<
      EditForm,
      'name' | 'description' | 'serialNumber' | 'modelName'
    >,
    value: string
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field in errors && errors[field as keyof EditFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setConfirmClearOpen(true);
    } else {
      onCancel();
    }
  };

  // ── Fake progress ─────────────────────────────────────────────────────────

  const startFakeProgress = useCallback((key: string, fileSize: number) => {
    const existing = fakeTimers.current.get(key);
    if (existing !== undefined) {
      clearInterval(existing);
    }

    const stepMs = FAKE_DURATION_MS / FAKE_STEPS;
    let step = 0;
    setUploadProgress((prev) => ({ ...prev, [key]: 0 }));

    const id = setInterval(() => {
      step += 1;
      const progress = Math.min((step / FAKE_STEPS) * fileSize, fileSize);
      setUploadProgress((prev) => ({ ...prev, [key]: progress }));

      if (step >= FAKE_STEPS) {
        clearInterval(id);
        fakeTimers.current.delete(key);
        setUploadProgress((prev) => {
          const { [key]: _done, ...rest } = prev;
          return rest;
        });
      }
    }, stepMs);

    fakeTimers.current.set(key, id);
  }, []);

  const stopFakeProgress = useCallback((key: string) => {
    const id = fakeTimers.current.get(key);
    if (id !== undefined) {
      clearInterval(id);
      fakeTimers.current.delete(key);
    }
    setUploadProgress((prev) => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // ── File select (fake progress only) ─────────────────────────────────────

  const handleImageChange = (files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }
    if (newImageFile) {
      stopFakeProgress(`image:${newImageFile.name}`);
    }
    setNewImageFile(file);
    setErrors((prev) => ({ ...prev, imageFile: '' }));
    startFakeProgress(`image:${file.name}`, file.size);
  };

  const handleDocsChange = (files: File[]) => {
    const newNames = new Set(files.map((f) => f.name));
    for (const prev of newDocFiles) {
      if (!newNames.has(prev.name)) {
        stopFakeProgress(`docs:${prev.name}`);
      }
    }
    for (const file of files) {
      if (!newDocFiles.find((f) => f.name === file.name)) {
        startFakeProgress(`docs:${file.name}`, file.size);
      }
    }
    setNewDocFiles(files);
  };

  // ── Upload at save time ───────────────────────────────────────────────────

  const uploadFile = async (
    file: File,
    slot: 'image' | 'docs'
  ): Promise<MediaResource> => {
    const pathTemplate = `equipments/entityid-placeholder/${slot}/resourceid-placeholder/${file.name}`;
    const presigned = await getPresignedUrlsBatch({
      paths: [pathTemplate],
      generatePath: true,
    });
    const fileData = presigned.files[0];
    if (!fileData) {
      throw new Error(EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
    }
    await uploadFileToS3(fileData.url, file);
    return {
      path: fileData.path,
      resourceId: fileData.resourceId,
      isUploaded: true,
      filename: file.name,
      fileType: file.type,
      fileSize: file.size,
    };
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const hasImage = existingImageMeta !== null || newImageFile !== null;
    const newErrors: EditFormErrors = {
      name: !form.name.trim() ? EQUIPMENT_ERROR_MESSAGES.REQUIRED : '',
      description: !form.description.trim()
        ? EQUIPMENT_ERROR_MESSAGES.REQUIRED
        : '',
      category: !form.category ? EQUIPMENT_ERROR_MESSAGES.REQUIRED : '',
      imageFile: !hasImage ? EQUIPMENT_ERROR_MESSAGES.REQUIRED : '',
      serialNumber: '',
    };
    if (Object.values(newErrors).some(Boolean)) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    onSavingChange?.(true);
    try {
      let newImageResource: MediaResource | null = null;
      if (newImageFile) {
        newImageResource = await uploadFile(newImageFile, 'image');
      }

      const newDocResources: Record<string, MediaResource> = {};
      for (const file of newDocFiles) {
        newDocResources[file.name] = await uploadFile(file, 'docs');
      }

      const payload: Parameters<typeof editEquipment>[1] = {
        name: form.name.trim(),
        description: form.description.trim(),
        ...(form.category && { category: form.category }),
        serialNumber: form.serialNumber.trim(),
        modelName: form.modelName.trim(),
        ...(form.dateOfProduction && {
          dateOfProduction: form.dateOfProduction.toISOString(),
        }),
      };

      if (newImageResource) {
        payload.image = newImageResource;
      } else if (existingImageMeta) {
        payload.image = {
          path: existingImageMeta.path,
          resourceId: existingImageMeta.resourceId,
          filename: existingImageMeta.filename,
          fileType: existingImageMeta.fileType,
          fileSize: existingImageMeta.fileSize,
          isUploaded: existingImageMeta.isUploaded,
        };
      }

      const existingDocs: MediaResource[] = existingDocsMeta.map((m) => ({
        path: m.path,
        resourceId: m.resourceId,
        filename: m.filename,
        fileType: m.fileType,
        fileSize: m.fileSize,
        isUploaded: m.isUploaded,
      }));
      const newDocs: MediaResource[] = newDocFiles
        .map((f) => newDocResources[f.name])
        .filter((r): r is MediaResource => r !== undefined);
      const docs = [...existingDocs, ...newDocs];
      payload.docs = docs;

      const result = await editEquipment(equipment.id, payload);
      if (!result.success) {
        if (result.fieldErrors) {
          setErrors((prev) => ({
            ...prev,
            serialNumber: result.fieldErrors?.serialNumber ?? '',
          }));
          return;
        }
        onError(result.error ?? EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR);
        return;
      }

      // Fire-and-forget: delete removed files from S3 after PATCH succeeds
      if (deletedPaths.current.length > 0) {
        void Promise.all(
          deletedPaths.current.map((path) => deleteMediaResource(path))
        );
        deletedPaths.current = [];
      }

      const updatedImage = newImageResource
        ? {
            filename: newImageResource.filename,
            fileSize: newImageResource.fileSize,
            fileType: newImageResource.fileType,
            path: newImageResource.path,
            resourceId: newImageResource.resourceId,
          }
        : equipment.image;

      onSaved({
        ...equipment,
        name: form.name.trim(),
        description: form.description.trim(),
        ...(form.category && { category: form.category }),
        categoryLabel:
          defaultCategories.find((c) => c.id === form.category)?.label ??
          searchCategories.find((c) => c.id === form.category)?.label ??
          equipment.categoryLabel ??
          '',
        serialNumber: form.serialNumber.trim() || undefined,
        modelName: form.modelName.trim() || undefined,
        ...(form.dateOfProduction && {
          dateOfProduction: form.dateOfProduction.toISOString(),
        }),
        image: updatedImage ?? null,
        docs:
          docs.length > 0
            ? docs.map((r) => ({
                filename: r.filename,
                fileSize: r.fileSize,
                fileType: r.fileType,
                path: r.path,
                resourceId: r.resourceId,
              }))
            : (equipment.docs ?? []),
      });
    } catch {
      onError(EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
    } finally {
      setIsSaving(false);
      onSavingChange?.(false);
    }
  };

  const isUploading = Object.keys(uploadProgress).length > 0;

  return (
    <div className="border border-primary-color-30 p-4 rounded-2xl flex flex-col gap-6">
      <CreateModal
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        onConfirmCancel={() => {
          setConfirmClearOpen(false);
          onCancel();
        }}
      />

      <p className="text-[18px] font-normal text-neutral-10">
        Edytuj aparaturę
      </p>

      {/* Name + Equipment ID */}
      <div className="flex gap-4 w-2/3">
        <div className="flex-1">
          <TextField
            fullWidth
            label={EQUIPMENT_LABELS.FIELD_NAME}
            dataTestId="edit-equipment-name"
            autoTrimMaxLength={200}
            value={form.name}
            onChange={(e) => updateField('name', e.target.value.trimStart())}
            error={Boolean(errors.name)}
            errorMessage={errors.name}
            disabled={isSaving}
          />
        </div>
        <div className="flex-1">
          <TextField
            fullWidth
            label="ID aparatury"
            value={equipment.equipmentId ?? ''}
            disabled
          />
        </div>
      </div>

      {/* Description */}
      <div className="w-full">
        <Textarea
          label={EQUIPMENT_LABELS.FIELD_DESCRIPTION}
          dataTestId="edit-equipment-description"
          maxLength={500}
          width="100%"
          rows={4}
          value={form.description}
          onChange={(e) =>
            updateField('description', e.target.value.trimStart().slice(0, 500))
          }
          error={Boolean(errors.description)}
          errorMessage={errors.description}
          disabled={isSaving}
        />
      </div>

      {/* Category | Model Name | Serial Number */}
      <div className="flex gap-4">
        <div className="flex-1 min-w-0">
          <Dropdown
            menuFullWidth
            menuMaxVisibleItems={4}
            placement="bottom-start"
            items={(() => {
              const displayed = categorySearch
                ? searchCategories
                : defaultCategories;
              // categorySearch !== debouncedCategorySearch covers the debounce window as a safety net.
              if (
                (categorySearchLoading ||
                  categorySearch !== debouncedCategorySearch) &&
                categorySearch
              ) {
                return [
                  { id: '__loading__', label: 'Ładowanie...', disabled: true },
                ];
              }
              if (categoryDefaultLoading && !categorySearch) {
                return [
                  ...displayed.map((o) => ({
                    id: o.id,
                    label: o.label,
                    toolTip: false,
                  })),
                  { id: '__loading__', label: 'Ładowanie...', disabled: true },
                ];
              }
              if (
                !categorySearchLoading &&
                categorySearch &&
                displayed.length === 0
              ) {
                return [
                  {
                    id: '__no_results__',
                    label: 'Nie znaleziono wyników',
                    disabled: true,
                  },
                ];
              }
              return displayed.map((o) => ({
                id: o.id,
                label: o.label,
                toolTip: false,
              }));
            })()}
            selectedId={form.category}
            onItemClick={(value) => {
              if (value !== '__loading__') {
                setForm((prev) => ({ ...prev, category: value }));
                setErrors((prev) => ({ ...prev, category: '' }));
              }
            }}
            disabled={isSaving}
            error={Boolean(errors.category)}
            errorMessage={errors.category}
            showSearch
            searchPlaceholder="Szukaj..."
            onSearchChange={(q) => {
              const trimmed = q.trim();
              if (trimmed === categorySearch) {
                return;
              }
              setCategorySearch(trimmed);
              if (!trimmed) {
                setSearchCategories([]);
                setCategorySearchLoading(false);
              } else {
                // Set here, not in the effect — useDebounce settling triggers a re-render before
                // the watching useEffect runs, leaving a gap where loading=false causes a flicker.
                setCategorySearchLoading(true);
              }
            }}
            onOpenChange={(open) => {
              if (!open) {
                setCategorySearch('');
                setSearchCategories([]);
                setCategorySearchLoading(false);
              }
            }}
            onScrollEnd={() => {
              if (
                !categorySearch &&
                categoryHasMore &&
                !categoryDefaultLoading
              ) {
                void fetchMoreCategories();
              }
            }}
            trigger={({ open, toggle, disabled: d, error: e }) => (
              <DropDownButton
                label={EQUIPMENT_LABELS.FIELD_CATEGORY}
                value={
                  defaultCategories.find((o) => o.id === form.category)
                    ?.label ??
                  searchCategories.find((o) => o.id === form.category)?.label ??
                  initialCategoryLabel ??
                  ''
                }
                open={open}
                toggle={toggle}
                disabled={d}
                error={e}
                data-testid="equipment-category"
              />
            )}
          />
        </div>
        <div className="flex-1">
          <TextField
            fullWidth
            label={optionalLabel(EQUIPMENT_LABELS.FIELD_MODEL_NAME)}
            dataTestId="equipment-model-name"
            autoTrimMaxLength={200}
            value={form.modelName}
            onChange={(e) =>
              updateField('modelName', e.target.value.trimStart())
            }
            disabled={isSaving}
          />
        </div>
        <div className="flex-1">
          <TextField
            fullWidth
            label={optionalLabel(EQUIPMENT_LABELS.FIELD_SERIAL_NUMBER)}
            dataTestId="equipment-serial-number"
            autoTrimMaxLength={200}
            value={form.serialNumber}
            onChange={(e) =>
              updateField('serialNumber', e.target.value.trimStart())
            }
            disabled={isSaving}
            error={Boolean(errors.serialNumber)}
            errorMessage={errors.serialNumber}
          />
        </div>
      </div>

      {/* Date of Production */}
      <div className="flex gap-4">
        <div className="flex-1" ref={datePickerRef}>
          <DatePickerField
            label={optionalLabel(EQUIPMENT_LABELS.FIELD_DATE_OF_PRODUCTION)}
            ariaLabel={EQUIPMENT_LABELS.FIELD_DATE_OF_PRODUCTION}
            pickerLabel={EQUIPMENT_LABELS.FIELD_DATE_OF_PRODUCTION_PICKER_LABEL}
            dataTestId="equipment-date-of-production"
            variant="flat"
            compact
            open={datePickerOpen}
            onOpenChange={setDatePickerOpen}
            value={form.dateOfProduction}
            onChange={(date) => {
              const utcDate = date
                ? new Date(
                    Date.UTC(
                      date.getFullYear(),
                      date.getMonth(),
                      date.getDate()
                    )
                  )
                : undefined;
              setForm((prev) => ({ ...prev, dateOfProduction: utcDate }));
            }}
            maxDate={new Date()}
            disabled={isSaving}
          />
        </div>
        <div className="flex-1" />
        <div className="flex-1" />
      </div>

      {/* Image */}
      <div className="flex flex-col gap-2">
        <p className="text-bodyLarge text-[#474A4C]">
          {EQUIPMENT_LABELS.FIELD_EQUIPMENT_IMAGE}
        </p>
        <FileUpload
          onFileSelect={handleImageChange}
          selectedFiles={newImageFile ? [newImageFile] : []}
          existingFiles={
            existingImageMeta
              ? [
                  {
                    filename: existingImageMeta.filename,
                    fileSize: existingImageMeta.fileSize,
                    path: existingImageMeta.path,
                  },
                ]
              : []
          }
          onExistingFileDelete={() => {
            if (existingImageMeta) {
              deletedPaths.current.push(existingImageMeta.path);
            }
            setExistingImageMeta(null);
          }}
          allowedTypes={[FileType.JPG, FileType.JPEG, FileType.PNG]}
          maxFiles={1}
          disabled={isSaving}
          error={Boolean(errors.imageFile)}
          errorMessage={errors.imageFile || undefined}
          uploadProgress={
            newImageFile &&
            uploadProgress[`image:${newImageFile.name}`] !== undefined
              ? {
                  [newImageFile.name]:
                    uploadProgress[`image:${newImageFile.name}`] ?? 0,
                }
              : undefined
          }
        />
      </div>

      {/* Docs */}
      <div className="flex flex-col gap-2">
        <p className="text-[#474A4C]">
          <span className="text-[16px] leading-[24px] tracking-[0.5px] font-normal">
            {EQUIPMENT_LABELS.SECTION_SAFETY_DOCUMENTS}
          </span>
          <span className="text-[12px] leading-[16px] tracking-[0.4px] font-normal">
            {' '}
            {EQUIPMENT_LABELS.SECTION_OPTIONAL}
          </span>
        </p>
        <FileUpload
          onFileSelect={handleDocsChange}
          selectedFiles={newDocFiles}
          existingFiles={existingDocsMeta.map((m) => ({
            filename: m.filename,
            fileSize: m.fileSize,
            path: m.path,
          }))}
          onExistingFileDelete={(path) => {
            deletedPaths.current.push(path);
            setExistingDocsMeta((prev) => prev.filter((m) => m.path !== path));
          }}
          allowedTypes={[
            FileType.JPG,
            FileType.JPEG,
            FileType.PNG,
            FileType.PDF,
            FileType.DOCX,
          ]}
          maxFiles={2}
          disabled={isSaving}
          uploadProgress={
            newDocFiles.length > 0
              ? Object.fromEntries(
                  newDocFiles
                    .filter(
                      (f) => uploadProgress[`docs:${f.name}`] !== undefined
                    )
                    .map((f) => [f.name, uploadProgress[`docs:${f.name}`] ?? 0])
                )
              : undefined
          }
        />
      </div>

      <div className="mt-[16px] -mx-4 border-b-2 border-[#DCDDE0]" />

      <div className="flex justify-end gap-4 mt-[-8px]">
        <Button
          label={FACILITY_LABELS.CANCEL_BUTTON}
          buttonType="Square"
          type="button"
          onClick={handleCancel}
          disabled={isSaving}
          className="!bg-transparent disabled:!bg-neutral-10/15 !px-3 !py-1.5 cursor-pointer border border-[#DCDDE0] !text-labelLarge !text-on-surface-variant disabled:!text-neutral-10/[0.38] hover:!border-[#DCDDE0] hover:!bg-[#474A4C]/[0.08] !rounded-[8px]"
        />
        <Button
          label={
            isSaving
              ? FACILITY_LABELS.EDIT_EMPLOYEE_SAVE_BUTTON_LOADING
              : FACILITY_LABELS.EDIT_EMPLOYEE_SAVE_BUTTON
          }
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || !isDirty || isUploading}
          className="!px-3 !py-1.5 text-labelLarge cursor-pointer !rounded-[8px]"
        />
      </div>
    </div>
  );
};
